<template>
  <tr-dialog
    ref="modal"
    :unique-name="true"
    name="payin-details-dialog"
    title="Detalhes da cobrança"
    :has-its-own-buttons="true"
  >
    <div
      :key="key"
      class="modal-container center-h-and-v block"
    >
      <tr-loading
        v-if="!payin?.id"
        classes="center-h-and-v width-100"
      />
      <div
        v-else
        class="payin-details-dialog"
      >
        <status-badge
          :status="payin.status"
          badge-size="md"
          icon-size="sm"
        />
        <bg-text
          v-if="payin.payer?.name"
          type="body-md"
          color="grey-dark"
          weight="bold"
        >
          Transfeera
        </bg-text>
        <bg-text
          type="body-md"
          color="grey-semi-dark"
          weight="bold"
        >
          {{ obfuscateAndFormatCPF(transfeeraData.CNPJ) }}
        </bg-text>
        <bg-text
          type="h4"
          color="green-semi-dark"
          weight="bold"
        >
          {{ centsToReal(payin.amount) }}
        </bg-text>
        <bg-button
          v-if="payin.receipt_file_url"
          v-tooltip.top-center="'Clique aqui para visualizar seu comprovante de recebimento'"
          aria-label="Clique aqui para visualizar seu comprovante de recebimento"
          variant="ghost"
          @click.stop="getReceipt(payin.receipt_file_url)"
        >
          <bg-icon
            size="md"
            name="bank"
            color="blue-normal"
          />
        </bg-button>
        <hr class="payin-details-dialog__hr">
        <info-action-box
          theme="bridge"
          type="info"
        >
          <bg-text type="body-sm">
            O valor apresentado é o total de um repasse feito pelo adquirente, reunindo diferentes
            cobranças.
          </bg-text>
        </info-action-box>
        <bg-text
          type="body-md"
          color="grey-dark"
          weight="bold"
        >
          Mais detalhes
        </bg-text>
        <payin-more-details-list
          :id="id"
          class="payins-details-dialog__more-details"
          :fine-type="payin.details?.fine_type"
          :fine-amount="payin.details?.fine_amount"
          :fine-percent="payin.details?.fine_percent"
          :interest-type="payin.details?.interest_type"
          :interest-amount="payin.details?.interest_amount"
          :interest-percent="payin.details?.interest_percent"
          :pix-key="payin.details?.pix_key"
          :payment-method="payin.payment_type"
          :created-at="payin.created_at"
          :updated-at="payin.updated_at"
          :received-at="payin.received_at"
          :due-date="payin.details?.due_date"
          :charge-id="payin.details?.charge_id"
          :payin-id="payin.id"
        />
      </div>
    </div>
  </tr-dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import StatusBadge from '@/apps/payin/components/StatusBadge.vue';
import { BgButton, BgIcon, BgText } from '@transfeeradev/bridge';
import { obfuscateAndFormatCPF } from '@/commons/formatters/cpfCnpj';
import { centsToReal } from '@/commons/formatters/currency';
import InfoActionBox from '@/commons/components/presentational/InfoActionBox.vue';
import { transfeeraData } from '@transfeeradev/api-enums';
import PayinMoreDetailsList from '@/apps/payin/payins/components/PayinMoreDetailsList.vue';
import { usePaymentMethod } from '@/apps/payin/payins/components/usePaymentMethod';

const key = ref();
const modal = ref();
const /** @type {Payin} */ payin = ref({});

const open = props => {
  payin.value = props.payin;
  modal.value.open();
};

const close = () => {
  modal.value.close();
};

const getReceipt = url => {
  window.open(url, '_blank');
};

const paymentType = computed(() => payin.value.payment_type);
const { paymentMethodMacro } = usePaymentMethod(paymentType);

const id = computed(() => {
  if (paymentMethodMacro.value === 'pix') {
    return payin.value.end_to_end_id;
  }

  if (paymentMethodMacro.value === 'boleto') {
    return payin.value.details?.digitable_line;
  }

  if (paymentMethodMacro.value === 'credit_card') {
    return payin.value.details?.authorization_code;
  }

  return '';
});

defineExpose({
  open,
  close
});
</script>

<style lang="postcss" scoped>
.payin-details-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--bg-spacing-sm);
}

.payin-details-dialog__hr {
  border: 1px solid var(--bg-grey-light);
  opacity: 0.7;
  width: 100%;
  margin: 0;
}

.payins-details-dialog__more-details {
  min-width: 350px;
  max-width: 400px;
}

.flex {
  display: flex;
}

.modal-container {
  min-height: 400px;
}
</style>
